import { IkePromiseQueue } from '@ikegps/core-shared';
import { action, computed, observable, makeObservable } from 'mobx';
import { T } from '../../i18n/index';
import { Logger } from '../log.service';
import { MAX_UPLOAD_FILE_SIZE_MB, validateUploadFile, type UploadValidationRules } from '../../util/upload.reader';

export const MAX_UPLOAD_BATCH_SIZE = 20;

const UPLOAD_CONCURRENCY = 3;

/** `invalid`/`skipped` are decided before sending and never retried; `failed` can be retried. */
export type UploadItemStatus = 'ready' | 'invalid' | 'skipped' | 'uploading' | 'done' | 'failed';

export interface UploadItemError {
	key: T;
	attrs?: { [key: string]: string | number };
}

export class UploadBatchItem {
	readonly file: File;
	readonly name: string;
	readonly validationError: UploadItemError | null;

	// Needs an initializer: with `useDefineForClassFields: false` an un-initialized field does not
	// exist when `makeObservable` runs, so it never becomes observable and the view stops updating.
	@observable status: UploadItemStatus = 'ready';
	@observable error: UploadItemError | null = null;

	private _previewUrl: string | null = null;

	constructor(file: File, validationError: UploadItemError | null) {
		makeObservable(this);

		this.file = file;
		this.name = file.name;
		this.validationError = validationError;

		if (validationError != null) {
			this.status = 'invalid';
			this.error = validationError;
		}
	}

	/** Null for non-images. The caller must `releasePreview` or the object URL leaks. */
	get previewUrl(): string | null {
		if (this._previewUrl != null) {
			return this._previewUrl;
		}
		if (!this.file.type.startsWith('image/')) {
			return null;
		}
		if (typeof URL === 'undefined' || typeof URL.createObjectURL !== 'function') {
			return null;
		}

		this._previewUrl = URL.createObjectURL(this.file);
		return this._previewUrl;
	}

	releasePreview() {
		if (this._previewUrl == null) {
			return;
		}

		URL.revokeObjectURL(this._previewUrl);
		this._previewUrl = null;
	}

	@computed get isSettled(): boolean {
		return this.status === 'done' || this.status === 'failed';
	}

	@computed get isExcluded(): boolean {
		return this.status === 'invalid' || this.status === 'skipped';
	}

	@action markReady() {
		this.status = 'ready';
		this.error = null;
	}

	@action markSkipped(error: UploadItemError) {
		this.status = 'skipped';
		this.error = error;
	}

	@action markUploading() {
		this.status = 'uploading';
		this.error = null;
	}

	@action markDone() {
		this.status = 'done';
		this.error = null;
	}

	@action markFailed(error: UploadItemError) {
		this.status = 'failed';
		this.error = error;
	}
}

/**
 * Files are validated on `add` but nothing is sent until `run`, so the caller can offer a review
 * step — an attached photo cannot be removed from a field afterwards.
 */
export class UploadBatch {
	@observable.shallow readonly items: UploadBatchItem[] = [];
	@observable private running: boolean = false;
	@observable private started: boolean = false;

	private readonly rules: UploadValidationRules;
	private readonly maxFiles: number;

	constructor(rules: UploadValidationRules, maxFiles: number = MAX_UPLOAD_BATCH_SIZE) {
		makeObservable(this);

		this.rules = rules;
		this.maxFiles = maxFiles;
	}

	@action add(files: FileList | File[]) {
		for (const file of Array.from(files)) {
			const validationError = this.validate(file);
			const item = new UploadBatchItem(file, validationError);

			if (validationError == null && !this.hasCapacity) {
				item.markSkipped({ key: T.UPLOAD_BATCH_OVER_LIMIT, attrs: { limit: this.maxFiles } });
			}

			this.items.push(item);
		}
	}

	/** Blocked once started: removing a sent file would misrepresent what is on the field. */
	@action remove(item: UploadBatchItem) {
		if (this.started) {
			return;
		}

		const index = this.items.indexOf(item);
		if (index === -1) {
			return;
		}

		item.releasePreview();
		this.items.splice(index, 1);
		this.promoteSkipped();
	}

	releasePreviews() {
		this.items.forEach((item) => item.releasePreview());
	}

	private validate(file: File): UploadItemError | null {
		const validationError = validateUploadFile(file, this.rules);
		if (validationError == null) {
			return null;
		}

		return { key: T[validationError], attrs: { limit: `${MAX_UPLOAD_FILE_SIZE_MB} MB` } };
	}

	/** Removing a file frees capacity, so an over-limit file can become uploadable again. */
	@action private promoteSkipped() {
		for (const item of this.items) {
			if (item.status !== 'skipped' || !this.hasCapacity) {
				continue;
			}
			item.markReady();
		}
	}

	@computed get countingTowardLimit(): number {
		return this.items.filter((item) => !item.isExcluded).length;
	}

	@computed get hasCapacity(): boolean {
		return this.countingTowardLimit < this.maxFiles;
	}

	@computed get total(): number {
		return this.items.length;
	}

	@computed get readyCount(): number {
		return this.items.filter((item) => item.status === 'ready').length;
	}

	@computed get successCount(): number {
		return this.items.filter((item) => item.status === 'done').length;
	}

	@computed get failureCount(): number {
		return this.items.filter((item) => item.status === 'failed').length;
	}

	@computed get invalidCount(): number {
		return this.items.filter((item) => item.status === 'invalid').length;
	}

	@computed get skippedCount(): number {
		return this.items.filter((item) => item.status === 'skipped').length;
	}

	@computed get excludedCount(): number {
		return this.items.filter((item) => item.isExcluded).length;
	}

	@computed get uploadTotal(): number {
		return this.items.filter((item) => !item.isExcluded).length;
	}

	@computed get settledCount(): number {
		return this.items.filter((item) => item.isSettled).length;
	}

	@computed get isRunning(): boolean {
		return this.running;
	}

	@computed get hasStarted(): boolean {
		return this.started;
	}

	@computed get isComplete(): boolean {
		return this.started && !this.running;
	}

	@computed get isCleanSweep(): boolean {
		return this.isComplete && this.failureCount === 0 && this.excludedCount === 0;
	}

	@action private setRunning(running: boolean) {
		this.running = running;
		if (running) {
			this.started = true;
		}
	}

	/** Never rejects — a failing file records itself so the rest of the batch still completes. */
	async run(upload: (item: UploadBatchItem) => Promise<void>): Promise<void> {
		if (this.running) {
			return;
		}

		this.setRunning(true);

		const queue = new IkePromiseQueue(Logger, UPLOAD_CONCURRENCY);

		for (const item of this.items) {
			if (item.status !== 'ready') {
				continue;
			}

			queue.add(async () => {
				item.markUploading();
				try {
					await upload(item);
					item.markDone();
				} catch (error) {
					Logger.error({ error }, 'Batch upload: file failed');
					item.markFailed({ key: T.ERROR_GENERAL });
				}
			});
		}

		await queue.join();

		this.setRunning(false);
	}

	/** Re-attempt only the files whose upload failed. Invalid files are never retried. */
	async retryFailed(upload: (item: UploadBatchItem) => Promise<void>): Promise<void> {
		if (this.running) {
			return;
		}

		this.items.filter((item) => item.status === 'failed').forEach((item) => item.markReady());

		await this.run(upload);
	}
}
